import { DocumentNode } from 'graphql'
import {
  computed,
  makeObservable,
  observable,
  ObservableMap,
  runInAction,
} from 'mobx'

import {
  DeleteLocationsDocument,
  UpdateLocationsPermittedCompaniesDocument,
} from '~/client/graph/operations/generated/LocationType.generated'
import { FORBIDDEN_SITE_NAME } from '~/client/src/shared/constants/locationName'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import ILocationDto from '~/client/src/shared/types/ILocationDto'
import { SITEMAP_ITEMS_COLORS_VALUES } from '~/client/src/shared/utils/SitemapItemsColors'

import IAccessibleLevelsAttribute from '../../models/IAccessibleLevelsAttribute'
import { computeAccessibleLevels } from '../../utils/accessibleLevels'
import * as e from '../EventStore/eventConstants'
import EventsStore from '../EventStore/Events.store'
import GraphExecutorStore from './GraphExecutor.store'
import IDeliveryAttributeWithColorStore from './interfaces/IDeliveryAttributeWithColorStore'

export default abstract class LocationBaseStore<
  T extends LocationBase<T>,
> implements IDeliveryAttributeWithColorStore {
  @observable public isDataReceived = false

  protected abstract saveMutation: DocumentNode
  protected deleteMutation: DocumentNode = DeleteLocationsDocument
  protected abstract graphName: string
  protected abstract defaultIconName: MapViewLocationIcon

  public constructor(
    protected eventsStore: EventsStore,
    protected graphExecutorStore: GraphExecutorStore,
  ) {
    makeObservable(this)
  }

  public abstract get byId(): Map<string, T>

  @computed
  public get listWithDeletedItems() {
    return Array.from(this.byId.values())
  }

  @computed
  public get list() {
    return this.listWithDeletedItems.filter(entry => !entry.isDeleted)
  }

  public clearList() {
    this.byId.clear()
    this.isDataReceived = false
  }

  public receiveList(list: ILocationDto[]) {
    this.clearList()

    list.forEach(dto => {
      const item = this.fromDto(dto)
      this.byId.set(item.id, item)
    })
    this.isDataReceived = true
  }

  public receiveOne(dto: ILocationDto) {
    const item = this.fromDto(dto)
    this.byId.set(item.id, item)
  }

  public clearFromStorage(id: string) {
    this.byId.delete(id)
  }

  public getInstanceById = (instanceId: string) => {
    const instance = this.byId.get(instanceId)
    if (instance && !instance.isDeleted) {
      return instance
    }
  }

  public async createFromName(name: string) {
    if (name === FORBIDDEN_SITE_NAME) {
      return
    }
    const item = this.fromDto({
      id: null,
      color: this.colorForNewItem,
      name,
      projectId: this.eventsStore.appState.activeProject.id,
      iconName: this.defaultIconName,
    })

    await this.saveItem(item)

    return item
  }

  public updateName(id: string, name: string) {
    if (name === FORBIDDEN_SITE_NAME) {
      return
    }
    const dto = this.byId.get(id)
    dto.name = name
    this.saveItem(dto)
  }

  public updateColor(id: string, color: string) {
    const dto = this.byId.get(id)
    dto.color = color
    this.saveItem(dto)
  }

  public async updateIfChanged(
    item: T,
  ): Promise<{ item: T; isUpdated: boolean }> {
    const existing = this.byId.get(item.id)
    if (existing && existing.isEqual(item)) {
      return { item, isUpdated: false }
    }

    item.id = await this.saveItem(item)
    return { item, isUpdated: true }
  }

  public async saveItem(
    item: T,
    shouldSkipAdditionalSaveAction?: boolean,
  ): Promise<string> {
    if (item.name === FORBIDDEN_SITE_NAME) {
      return
    }
    const oldItem = this.byId.get(item.id)

    runInAction(() => {
      this.loadingMap.set(e.SAVE_LOCATION, true)
    })

    const res = await this.graphExecutorStore.mutate(this.saveMutation, {
      [this.graphName]: [item.getDto()],
    })

    runInAction(() => {
      this.loadingMap.set(e.SAVE_LOCATION, false)
    })

    const [data] = Object.values<any>(res.data)[0]

    if (!item.id) {
      item.id = data.id
    }

    if (!shouldSkipAdditionalSaveAction) {
      await this.additionalSaveAction(item, oldItem)
    }

    return data.id || item.id
  }

  public async updatePermittedCompanies(
    id: string,
    permittedCompanies: string[],
  ): Promise<string[]> {
    const { data } = await this.graphExecutorStore.mutate(
      UpdateLocationsPermittedCompaniesDocument,
      {
        projectId: this.eventsStore.appState.activeProject.id,
        locationIds: [id],
        permittedCompanies,
      },
    )

    // The backend returns the ids it actually updated; ids that were stale,
    // belonged to another project, or no longer exist are omitted. Callers use
    // the returned ids to detect and reconcile partial updates.
    return (data?.updateLocationsPermittedCompanies ?? []).map(String)
  }

  public async removeItems(ids: string[]) {
    await this.graphExecutorStore.mutate(this.deleteMutation, { ids: ids })

    runInAction(() => {
      ids.forEach(id => this.byId.delete(id))
    })
  }

  public getString = (itemId: any, shouldShowDeletedEquipment: boolean) => {
    let result = '—'
    const item = this.byId.get(itemId)
    if (item && (!item.isDeleted || shouldShowDeletedEquipment)) {
      result = item.name
    }

    return result
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async additionalSaveAction(item: T, oldItem?: T): Promise<void> {
    return
  }

  protected abstract fromDto(dto: ILocationDto): T

  public get colorForNewItem(): string {
    const colorsCount = SITEMAP_ITEMS_COLORS_VALUES.length
    const nextColorIdx = this.list.length % colorsCount
    return SITEMAP_ITEMS_COLORS_VALUES[nextColorIdx]
  }

  protected get loadingMap(): ObservableMap<any, boolean> {
    return this.eventsStore.appState.loading
  }

  protected getAllowedAccessibleLevels(item: IAccessibleLevelsAttribute) {
    const { levels, buildings } = this.eventsStore.appState
    return computeAccessibleLevels(
      item.parent?.parentId,
      buildings,
      levels,
      item.accessibleLevels,
    )
  }
}
